import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowRight, MessageCircle, X } from 'lucide-react';
import { companionApi } from '../../services/companionApi';
import { aaiApi } from '../../services/aaiApi';
import type { CompanionLens, CompanionMood, CompanionSummary, CompanionTask } from '../../services/companionApi';
import { SessionDeck } from './SessionDeck';
import { CompanionChat } from './CompanionChat';
import './companion.css';

/*
 * The AI Companion: a small living guide in the corner of every screen the
 * Owner, AI Champion, department heads and employees use. It counts what waits on
 * the person and takes them to the page where it is done. It never blocks the
 * page and never acts on its own.
 */

export const COMPANION_NAME = 'Orbit';

const POSITION_KEY = 'aa-companion-position';
const EDGE = 20;
const SIZE = 58;
const DRAG_THRESHOLD = 4;
const GAP = 12;
const PANEL_WIDTH = 340;
/* Answering a session needs room to read the question and write a reply. */
const ANSWER_WIDTH = 440;
/* A chat needs a steady height, or the panel jumps with every message. */
const CHAT_HEIGHT = 560;
const BUBBLE_WIDTH = 280;
/* Above the sidebar (100), the topbar (80) and its dropdowns (90), below the
   Orbit drawer and its backdrop (105, 110), page drawers (190), modals (200)
   and toasts: never hidden by the chrome it is dragged over, never on top of
   a panel the person opened. */
const LAYER = 'z-[103]';

export const refreshCompanion = () => window.dispatchEvent(new Event('companion:refresh'));

const LENS_LINE: Record<CompanionLens, string> = {
  owner: 'For you as Owner',
  champion: 'For you as AI Champion',
  head: 'For you as department head',
  employee: 'For you',
};

type Offset = { right: number; bottom: number };

const readOffset = (): Offset => {
  try {
    const raw = localStorage.getItem(POSITION_KEY);
    if (!raw) return { right: EDGE, bottom: EDGE };
    const parsed = JSON.parse(raw) as Offset;
    return clampOffset(parsed);
  } catch {
    return { right: EDGE, bottom: EDGE };
  }
};

const clampOffset = (o: Offset): Offset => {
  const maxRight = Math.max(EDGE, window.innerWidth - SIZE - EDGE);
  const maxBottom = Math.max(EDGE, window.innerHeight - SIZE - EDGE);
  return {
    right: Math.min(Math.max(EDGE, o.right), maxRight),
    bottom: Math.min(Math.max(EDGE, o.bottom), maxBottom),
  };
};

/* Where the bubble and the panel open: on the side of the companion with
   more room, aligned to its outer edge, and kept inside the viewport. */
const popoverStyle = (o: Offset, wanted: number): React.CSSProperties => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const width = Math.min(wanted, vw - EDGE * 2);
  const top = vh - o.bottom - SIZE;
  const left = vw - o.right - SIZE;
  const style: React.CSSProperties = { width };
  const roomAbove = top - EDGE - GAP;
  const roomBelow = vh - (top + SIZE) - EDGE - GAP;
  if (roomAbove >= roomBelow) {
    style.bottom = o.bottom + SIZE + GAP;
    style.maxHeight = roomAbove;
  } else {
    style.top = top + SIZE + GAP;
    style.maxHeight = roomBelow;
  }
  if (left + SIZE / 2 >= vw / 2) {
    style.right = Math.min(o.right, vw - EDGE - width);
  } else {
    style.left = Math.min(left, vw - EDGE - width);
  }
  return style;
};

const saveOffset = (o: Offset) => {
  try {
    localStorage.setItem(POSITION_KEY, JSON.stringify(o));
  } catch {
    // Private windows refuse storage; the companion just starts in the corner next time.
  }
};

const plural = (n: number, one: string, many: string) => {
  if (n === 1) return `1 ${one}`;
  return `${n} ${many}`;
};

const nudgeLine = (summary: CompanionSummary): { title: string; body: string; link: string; key: string } => {
  const session = summary.new_sessions[0];
  if (session) {
    return {
      title: `${session.sender} sent you new questions`,
      body: `"${session.title}": ${plural(session.open, 'question', 'questions')} waiting for you.`,
      link: session.link,
      key: `session:${session.id}`,
    };
  }
  const first = summary.tasks[0];
  let body = first.title;
  if (summary.tasks.length > 1) body = `${first.title}, and ${plural(summary.tasks.length - 1, 'more thing', 'more things')}.`;
  return { title: `${plural(summary.total, 'thing needs', 'things need')} you`, body, link: first.link, key: first.key };
};

const Face: React.FC<{ mood: CompanionMood; small?: boolean }> = ({ mood, small = false }) => {
  let eye = 'aa-companion-eye';
  if (mood === 'snoozed') eye = 'aa-companion-eye sleepy';
  let size = 'h-[38px] w-[38px] rounded-[11px] gap-[6px]';
  if (small) size = 'h-[30px] w-[30px] rounded-[9px] gap-[5px]';
  return (
    <span className={`inline-flex shrink-0 items-center justify-center bg-accent ${size}`} aria-hidden>
      <i className={eye} />
      <i className={eye} />
    </span>
  );
};

const TaskRow: React.FC<{ task: CompanionTask; fresh: boolean; onOpen: (task: CompanionTask) => void }> = ({ task, fresh, onOpen }) => (
  <button
    type="button"
    onClick={() => onOpen(task)}
    className="group flex w-full items-center gap-3 rounded-card bg-card px-3 py-2.5 text-left transition-colors hover:bg-[#e3ebff] focus-visible:outline focus-visible:outline-2 focus-visible:outline-ink"
  >
    <span className="min-w-0 flex-1">
      <span className="flex min-w-0 items-center gap-1.5">
        <b className="min-w-0 truncate text-[13px] font-semibold text-ink" title={task.title}>{task.title}</b>
        {fresh && <span className="rounded-full bg-accent px-1.5 py-px text-[10px] font-semibold text-ink">New</span>}
      </span>
      <span className="mt-0.5 line-clamp-3 text-[11.5px] leading-[16px] text-ink-muted [overflow-wrap:anywhere]">{task.detail}</span>
    </span>
    <span className="shrink-0 text-right">
      <span className="block text-[12px] font-semibold tabular-nums text-ink">{task.count}</span>
      <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-ink-muted group-hover:text-ink">
        {task.cta}
        <ArrowRight size={11} aria-hidden />
      </span>
    </span>
  </button>
);

export const AiCompanion: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [summary, setSummary] = useState<CompanionSummary | null>(null);
  const [mood, setMood] = useState<CompanionMood>('clear');
  const [panelOpen, setPanelOpen] = useState(false);
  const [bubbleClosed, setBubbleClosed] = useState(false);
  const [offset, setOffset] = useState<Offset>(readOffset);
  const drag = useRef<{ x: number; y: number; start: Offset; moved: boolean; last: Offset } | null>(null);
  const [answering, setAnswering] = useState<CompanionTask | null>(null);
  const [chatting, setChatting] = useState(false);
  const handledLink = useRef('');

  const load = useCallback(async () => {
    try {
      const { data } = await companionApi.get();
      setSummary(data);
      setMood(data.mood);
      if (data.mood === 'nudge') setBubbleClosed(false);
    } catch {
      setSummary(null);
    }
  }, []);

  useEffect(() => { load(); }, [load, location.search]);

  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    window.addEventListener('companion:refresh', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('companion:refresh', onFocus);
    };
  }, [load]);

  /* The email and notification for a session link here with
     ?companion=session:<id>: open the panel on that session's cards, once. */
  useEffect(() => {
    const target = new URLSearchParams(location.search).get('companion') || '';
    if (!summary || !target.startsWith('session:') || handledLink.current === location.search) return;
    handledLink.current = location.search;
    const task = summary.tasks.find((t) => t.key === target && t.session);
    if (!task) return;
    setAnswering(task);
    setChatting(false);
    setPanelOpen(true);
    setBubbleClosed(true);
  }, [summary, location.search]);

  useEffect(() => {
    const onResize = () => setOffset((o) => clampOffset(o));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  if (!summary || !summary.on) return null;

  const markSeen = () => {
    let next: CompanionMood = 'clear';
    if (summary.total > 0) next = 'waiting';
    setMood(next);
    setSummary({ ...summary, new_sessions: [] });
    companionApi.seen(summary.total).catch(() => undefined);
  };

  const go = (link: string) => {
    setPanelOpen(false);
    setBubbleClosed(true);
    markSeen();
    navigate(link);
  };

  const openTask = (task: CompanionTask) => {
    if (task.kind === 'reply' && task.notification_id) {
      aaiApi.markNotificationsRead([task.notification_id]).catch(() => undefined);
      go(task.link);
      return;
    }
    if (task.kind === 'session' && task.session) {
      setAnswering(task);
      setPanelOpen(true);
      setBubbleClosed(true);
      markSeen();
      return;
    }
    go(task.link);
  };

  const openNudge = (n: { link: string; key: string }) => {
    const task = summary.tasks.find((t) => t.key === n.key);
    if (task) {
      openTask(task);
      return;
    }
    go(n.link);
  };

  const sent = () => {
    setAnswering(null);
    toast.success('Answers sent to your partner.');
    load();
  };

  const togglePanel = () => {
    const opening = !panelOpen;
    setPanelOpen(opening);
    if (!opening) setAnswering(null);
    if (!opening) setChatting(false);
    setBubbleClosed(true);
    if (opening) markSeen();
  };

  const snooze = () => {
    setPanelOpen(false);
    setBubbleClosed(true);
    setMood('snoozed');
    companionApi.snooze(summary.total).catch(() => undefined);
  };

  const onPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return;
    drag.current = { x: e.clientX, y: e.clientY, start: offset, moved: false, last: offset };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.x;
    const dy = e.clientY - d.y;
    if (!d.moved && Math.abs(dx) + Math.abs(dy) < DRAG_THRESHOLD) return;
    d.moved = true;
    d.last = clampOffset({ right: d.start.right - dx, bottom: d.start.bottom - dy });
    setOffset(d.last);
  };
  const onPointerUp = () => {
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    if (d.moved) {
      saveOffset(d.last);
      return;
    }
    togglePanel();
  };
  const onPointerCancel = () => {
    const d = drag.current;
    drag.current = null;
    if (d && d.moved) saveOffset(d.last);
  };

  let panelWidth = PANEL_WIDTH;
  if (answering || chatting) panelWidth = ANSWER_WIDTH;
  const panelStyle = popoverStyle(offset, panelWidth);
  if (chatting) panelStyle.height = Math.min(CHAT_HEIGHT, Number(panelStyle.maxHeight) || CHAT_HEIGHT);

  const showBubble = mood === 'nudge' && !bubbleClosed && !panelOpen && summary.total > 0;
  const nudge = showBubble && nudgeLine(summary);
  const freshIds = new Set(summary.new_sessions.map((s) => `session:${s.id}`));

  let label = `Open ${COMPANION_NAME}`;
  if (summary.total > 0) label = `Open ${COMPANION_NAME}, ${plural(summary.total, 'thing waits', 'things wait')} for you`;

  let panelSub = LENS_LINE[summary.lens || 'employee'];
  if (mood === 'snoozed') panelSub = 'Quiet until tomorrow';

  return createPortal(
    <>
      {nudge && (
        <div
          className={`aa-companion-bubble fixed ${LAYER} overflow-y-auto rounded-card bg-ink p-3 pr-8 text-white shadow-aa-md`}
          style={popoverStyle(offset, BUBBLE_WIDTH)}
          role="status"
        >
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => { setBubbleClosed(true); markSeen(); }}
            className="absolute right-2 top-2 rounded p-0.5 text-white/60 hover:text-white"
          >
            <X size={14} aria-hidden />
          </button>
          <button type="button" onClick={() => openNudge(nudge)} className="block w-full text-left">
            <b className="block text-[13px] font-semibold leading-[18px]">{nudge.title}</b>
            <span className="mt-0.5 block text-[12px] leading-[17px] text-white/75">{nudge.body}</span>
            <span className="mt-2 inline-flex items-center gap-1 text-[12px] font-semibold text-accent">
              Take me there
              <ArrowRight size={12} aria-hidden />
            </span>
          </button>
        </div>
      )}

      {panelOpen && (
        <aside
          className={`aa-companion-bubble fixed ${LAYER} flex flex-col overflow-hidden rounded-hero bg-white shadow-aa-md ring-1 ring-line`}
          style={panelStyle}
          aria-label={COMPANION_NAME}
        >
          <header className="flex shrink-0 items-center gap-2.5 border-b border-line px-4 py-3">
            <Face mood={mood} small />
            <div className="min-w-0 flex-1">
              <b className="block text-[14px] leading-[18px] text-ink">{COMPANION_NAME}</b>
              <small className="block text-[11.5px] text-ink-muted">{panelSub}</small>
            </div>
            <button type="button" aria-label="Close" onClick={() => { setPanelOpen(false); setAnswering(null); setChatting(false); }} className="rounded p-1 text-ink-soft hover:text-ink">
              <X size={15} aria-hidden />
            </button>
          </header>
          {answering && answering.session && (
            <div className="flex min-h-0 flex-1 flex-col">
              <SessionDeck
                key={answering.key}
                title={answering.title}
                session={answering.session}
                onBack={() => setAnswering(null)}
                onSent={sent}
              />
            </div>
          )}
          {chatting && !answering && (
            <div className="flex min-h-0 flex-1 flex-col">
              <CompanionChat onBack={() => setChatting(false)} onOpen={go} />
            </div>
          )}
          {!answering && !chatting && (
          <div className="grid min-h-0 flex-1 grid-cols-[minmax(0,1fr)] content-start gap-2 overflow-y-auto overflow-x-hidden overscroll-contain p-3">
            <button
              type="button"
              onClick={() => setChatting(true)}
              className="group flex w-full items-center gap-3 rounded-card border border-line bg-white px-3 py-2.5 text-left transition-colors hover:border-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-ink"
            >
              <MessageCircle size={16} className="shrink-0 text-ink" aria-hidden />
              <span className="min-w-0 flex-1">
                <b className="block text-[13px] font-semibold text-ink">Facing a problem?</b>
                <span className="mt-0.5 block text-[11.5px] leading-[16px] text-ink-muted">Talk it through with Orbit, or ask how anything here works.</span>
              </span>
              <ArrowRight size={12} className="shrink-0 text-ink-muted group-hover:text-ink" aria-hidden />
            </button>
            {summary.tasks.length === 0 && (
              <p className="m-0 px-1 py-2 text-[13px] leading-[19px] text-ink-muted">
                <b className="block text-ink">You are caught up.</b>
                I will tell you when your partner sends something new.
              </p>
            )}
            {summary.tasks.map((t) => <TaskRow key={t.key} task={t} fresh={freshIds.has(t.key)} onOpen={openTask} />)}
          </div>
          )}
          {!answering && !chatting && summary.total > 0 && mood !== 'snoozed' && (
            <footer className="shrink-0 border-t border-line px-4 py-2.5">
              <button type="button" onClick={snooze} className="text-[12px] font-semibold text-ink-muted hover:text-ink">
                Remind me tomorrow
              </button>
            </footer>
          )}
        </aside>
      )}

      <button
        type="button"
        aria-label={label}
        aria-expanded={panelOpen}
        title={COMPANION_NAME}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); togglePanel(); } }}
        className={`fixed ${LAYER} grid h-[58px] w-[58px] cursor-grab touch-none select-none place-items-center rounded-[19px] bg-ink shadow-[0_12px_30px_rgba(0,0,0,0.2)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink active:cursor-grabbing`}
        style={{ right: offset.right, bottom: offset.bottom }}
      >
        <Face mood={mood} />
        <img
          src="/aa-favicon.png"
          alt=""
          draggable={false}
          className="absolute -bottom-1.5 -right-1.5 h-[22px] w-[22px] rounded-[6px] ring-2 ring-white"
        />
        {summary.total > 0 && (
          <span className="absolute -right-2 -top-2 min-w-[22px] rounded-full border-2 border-ink bg-accent px-1.5 text-center text-[11px] font-semibold leading-[18px] tabular-nums text-ink">
            {summary.total}
          </span>
        )}
      </button>
    </>,
    document.body,
  );
};

export default AiCompanion;
